import React from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from '@mui/material'
import {
  styled,
  createTheme,
  ThemeProvider,
  keyframes,
} from '@mui/material/styles'
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ImportContactsRoundedIcon from '@mui/icons-material/ImportContactsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import { useNavigate } from 'react-router-dom'
import { useSupabase } from '../context/SupabaseContext'
// Custom theme matching your app colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#ffda1b', // gcc-gold
      contrastText: '#232536', // gcc-dark
    },
    secondary: {
      main: '#232536', // gcc-dark
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#232536', // gcc-dark
      secondary: '#6b7280', // gray-500
    },
    success: {
      main: '#10b981', // emerald-500
    },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      '@media (min-width:600px)': {
        fontSize: '3rem',
      },
      '@media (min-width:900px)': {
        fontSize: '3.5rem',
      },
      '@media (min-width:1200px)': {
        fontSize: '4rem',
      },
    },
    body1: {
      fontSize: '1rem',
      '@media (min-width:600px)': {
        fontSize: '1.125rem',
      },
      '@media (min-width:900px)': {
        fontSize: '1.25rem',
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
})

// Custom animations
const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const checkMark = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`

// Styled components
const MainContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'white',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage: `radial-gradient(circle at 1px 1px, rgba(35, 37, 54, 0.06) 1px, transparent 0)`,
    backgroundSize: '20px 20px',
    [theme.breakpoints.down('sm')]: {
      backgroundSize: '15px 15px',
    },
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `radial-gradient(circle at center, transparent 0%, rgba(255, 255, 255, 0.8) 70%)`,
  },
}))

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 10,
  width: '100%',
  maxWidth: '600px',
  textAlign: 'center',
  animation: `${fadeUp} 0.8s ease-out`,
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
}))

const CheckIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  '& .check-icon': {
    animation: `${checkMark} 0.8s ease-out 0.3s both`,
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(4),
  },
}))

const InfoCard = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(255, 218, 27, 0.08)',
  border: '1px solid rgba(255, 218, 27, 0.2)',
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(3),
  margin: theme.spacing(0, 1),
  textAlign: 'left',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
    margin: 0,
  },
}))

const LaunchButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffda1b 0%, #f59e0b 100%)',
  color: '#232536',
  fontWeight: 600,
  fontSize: '1rem',
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(1),
  boxShadow: '0 4px 12px rgba(255, 218, 27, 0.3)',
  transition: 'all 0.3s ease',
  animation: `${pulse} 2s infinite`,
  '&:hover': {
    background: 'linear-gradient(135deg, #f59e0b 0%, #ffda1b 100%)',
    boxShadow: '0 6px 20px rgba(255, 218, 27, 0.4)',
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.125rem',
    padding: theme.spacing(2, 5),
  },
}))

const BackButton = styled(Button)(({ theme }) => ({
  border: '2px solid #232536',
  color: '#232536',
  fontWeight: 500,
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.spacing(1),
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#ffda1b',
    borderColor: '#ffda1b',
    color: '#232536',
    '& .arrow-icon': {
      transform: 'translateX(-4px)',
    },
  },
  '& .arrow-icon': {
    transition: 'transform 0.2s ease',
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.5, 4),
  },
}))

const ThankYou = () => {
  const { signOut } = useSupabase()
  const navigate = useNavigate()
  const handleStartExploring = async () => {
    await signOut()
    navigate('/')
  }

  const features = [
    {
      icon: <SearchRoundedIcon sx={{ color: '#10b981' }} />,
      text: 'Advanced search across all GCC tax regulations',
    },
    {
      icon: <ImportContactsRoundedIcon sx={{ color: '#10b981' }} />,
      text: 'Comprehensive tax law database and documentation',
    },
    {
      icon: <TrendingUpRoundedIcon sx={{ color: '#10b981' }} />,
      text: 'Real-time updates and regulatory changes',
    },
    {
      icon: <EmailRoundedIcon sx={{ color: '#10b981' }} />,
      text: 'Personalized notifications and alerts',
    },
  ]

  return (
    <ThemeProvider theme={theme}>
      <MainContainer>
        <Container maxWidth='sm'>
          <ContentWrapper>
            {/* Success Icon */}
            <CheckIconWrapper>
              <CheckCircleRoundedIcon
                className='check-icon'
                sx={{
                  fontSize: { xs: '4rem', sm: '5rem' },
                  color: 'success.main',
                }}
              />
            </CheckIconWrapper>

            {/* Welcome Message */}
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography
                variant='h1'
                sx={{
                  color: 'text.primary',
                  lineHeight: 1.2,
                  mb: 2,
                  px: 1,
                }}
              >
                Welcome to GCC Tax Laws!
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  color: 'text.primary',
                  lineHeight: 1.6,
                  maxWidth: { xs: '90%', sm: '100%' },
                  mx: 'auto',
                  px: 1,
                }}
              >
                Your account is now active and ready to use. Start exploring our
                comprehensive tax research platform.
              </Typography>
            </Box>

            {/* Launch CTA */}
            <Box sx={{ mb: { xs: 4, sm: 5 } }}>
              <Typography
                variant='body2'
                sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
              >
                All features are now available to you
              </Typography>
            </Box>

            {/* Features Card */}
            <InfoCard sx={{ mb: { xs: 4, sm: 5 } }}>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                }}
              >
                What you can do now:
              </Typography>
              <List dense sx={{ py: 0 }}>
                {features.map((feature, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: '32px' }}>
                      {feature.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={feature.text}
                      primaryTypographyProps={{
                        fontSize: { xs: '0.875rem', sm: '0.9rem' },
                        color: 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </InfoCard>

            {/* Back to Home Button */}
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <LaunchButton
                fullWidth
                onClick={handleStartExploring}
                startIcon={<RocketLaunchRoundedIcon />}
                sx={{ mb: 2 }}
              >
                Start Exploring Platform
              </LaunchButton>
              {/* <BackButton
                onClick={handleSignOut}
                startIcon={
                  <ArrowBackRoundedIcon
                    className='arrow-icon'
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  />
                }
                fullWidth={{ xs: true, sm: false }}
              >
                <Typography
                  variant='body2'
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  Back to Home
                </Typography>
              </BackButton> */}
            </Box>

            {/* Support Contact */}
            <Box>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                Need help getting started?{' '}
                <Link
                  href='mailto:support@gcctaxlaws.com'
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      color: 'text.primary',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Contact Support
                </Link>
              </Typography>
            </Box>
          </ContentWrapper>
        </Container>
      </MainContainer>
    </ThemeProvider>
  )
}

export default ThankYou
